import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialLayoutEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {SettingsHelpAndTipsWidget} from './settingsHelpAndTipsWidget';

/*
 * Props.
 */

interface SettingsHelpWidgetProps {
  panelId: AppPanelId;
  anchor: Anchor;
  onRequestClose: () => void;
  isGlobalAdmin: boolean;
  onFrontAcademyClick: () => void;
  onHelpClick: () => void;
  canEmail: boolean;
  onEmailClick: () => void;
  onOpenClick: () => void;
  useDropdownStyle?: boolean;
}

interface SettingsHelpWidgetStyleProps {
  $useDropdownStyle: boolean;
}
const StyledWidgetDiv = styled.div<SettingsHelpWidgetStyleProps>`
  position: relative;
  bottom: 11px;

  ${(p) => applyDropdownStyles(p.$useDropdownStyle)}
`;

function applyDropdownStyles(isDropdown: boolean) {
  if (isDropdown) {
    return css`
      background-color: ${(p) => p.theme.foreground};
      border-radius: 16px;
      ${shadows.dialogsAndDropdowns};
      bottom: 0;
      margin-top: 8px;
    `;
  }

  return css`
    background-color: ${(p) => p.theme.greys.shade20};
    border-radius: 16px;
    ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
  `;
}

const StyledCloseButton = styled(ChromelessButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 10px;

  width: 30px;
  height: 30px;
  ${center()};

  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    color: ${(p) => p.theme.greys.shade70};
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

/*
 * Component.
 */

export const SettingsHelpWidget: FC<SettingsHelpWidgetProps> = (props) => {
  const {onOpenClick} = props;

  // Call `onOpenClick` on first layout effect.
  useInitialLayoutEffect(() => onOpenClick());

  // NOTE - the SVGs below are the `closeSmall` and `helpFilledSelected` icons.
  // Due to a problem with the Chromium version of our desktop app, these SVGs
  // do not display properly when using the Icon component.
  // Therefore until we upgrade the Chromium version, we are embedding these SVGs directly.
  return (
    <RepositionPopover
      anchor={props.anchor}
      onRequestClose={props.onRequestClose}
      placement="top-end"
      isInteractive
      isExclusive
    >
      <StyledWidgetDiv $useDropdownStyle={Boolean(props.useDropdownStyle)}>
        <StyledCloseButton onClick={props.onRequestClose}>
          <TooltipCoordinator
            render={() => <Trans id="onboarding-widget-close">Close</Trans>}
            placement="top"
          >
            <Icon name="close" />
          </TooltipCoordinator>
        </StyledCloseButton>
        <SettingsHelpAndTipsWidget panelId={props.panelId} />
      </StyledWidgetDiv>
    </RepositionPopover>
  );
};
